import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Alert, FormField } from '@/shared/ui';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { useResetPassword } from '../api/use-auth.mutation';
import { toUserMessage } from '@platform/api-client';
import {
  resetPasswordSchema,
  type ResetPasswordFormValues,
} from '../schemas/auth.schemas';

/**
 * 비밀번호 재설정 View.
 *
 * 메일 링크 형식:
 *   {VITE_MAIN_FRONT_URL}/auth/reset-password?token=xxxx
 *
 * 토큰은 백엔드 PasswordReset 엔티티에 해시로 저장되어 있고,
 * 성공적으로 사용되면 usedAt 이 기록되어 재사용이 불가하다.
 */
export function ResetPasswordView() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [form, setForm] = useState<ResetPasswordFormValues>({
    newPassword: '',
    passwordConfirm: '',
  });
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof ResetPasswordFormValues, string>>
  >({});

  const { mutate, isPending, isSuccess, error, reset } = useResetPassword();

  const onChange =
    (key: keyof ResetPasswordFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
      if (fieldErrors[key]) {
        setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
      }
      if (error) reset();
    };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!token) return;

    const parsed = resetPasswordSchema.safeParse(form);
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      setFieldErrors({
        newPassword: flat.newPassword?.[0],
        passwordConfirm: flat.passwordConfirm?.[0],
      });
      return;
    }

    mutate({ token, newPassword: parsed.data.newPassword });
  };

  if (!token) {
    return (
      <div className="w-full max-w-md mx-auto py-10 text-center">
        <h1 className="text-2xl font-bold text-adobe-dark mb-4 dark:text-neutral-100">
          유효하지 않은 링크
        </h1>
        <Alert tone="error">
          토큰이 없거나 링크가 손상되었습니다. 비밀번호 재설정을 다시 요청해주세요.
        </Alert>
        <Link
          to={PATHS.REQUEST_PASSWORD_RESET}
          className="inline-block mt-6 text-primary hover:underline"
        >
          재설정 링크 다시 받기
        </Link>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="w-full max-w-md mx-auto py-10 text-center">
        <h1 className="text-2xl font-bold text-adobe-dark mb-4 dark:text-neutral-100">
          비밀번호가 변경되었습니다
        </h1>
        <p className="text-adobe-muted dark:text-neutral-400">새 비밀번호로 다시 로그인해주세요.</p>
        <button
          type="button"
          onClick={() => navigate(PATHS.LOGIN, { replace: true })}
          className="mt-6 w-full py-2 bg-adobe-red text-white font-bold rounded"
        >
          로그인 페이지로 이동
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm mx-auto py-10">
      <h1 className="text-2xl font-bold text-adobe-dark mb-6 dark:text-neutral-100">비밀번호 재설정</h1>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <FormField
          label="새 비밀번호"
          type="password"
          name="newPassword"
          autoComplete="new-password"
          required
          value={form.newPassword}
          onChange={onChange('newPassword')}
          error={fieldErrors.newPassword}
          hint="영문과 숫자를 포함해 8자 이상"
        />

        <FormField
          label="새 비밀번호 확인"
          type="password"
          name="passwordConfirm"
          autoComplete="new-password"
          required
          value={form.passwordConfirm}
          onChange={onChange('passwordConfirm')}
          error={fieldErrors.passwordConfirm}
        />

        {error && <Alert tone="error">{toUserMessage(error)}</Alert>}

        <button
          type="submit"
          disabled={isPending}
          className="w-full py-2 bg-adobe-red text-white font-bold rounded disabled:opacity-50"
        >
          {isPending ? '변경 중...' : '비밀번호 변경'}
        </button>
      </form>
    </div>
  );
}
